import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import {
    MintPrice,
    mintPrice,
    mintStatus,
    requestMint,
    SkinMintError,
    solscanUrl,
} from '../api/skinMint'
import { assetAnimationUrl, assetUrl, itemName } from '../utils/assetUrl'

/**
 * The offer to put a skin on chain.
 *
 * A skin already works in game and already earns, so this is not a fix for
 * something broken — it is the one thing a skin cannot do. Owning it outright
 * and selling it to another player is the whole difference, and the copy says
 * that rather than talking about mints and wallets.
 *
 * Portalled to the body for the same reason `ItemDetail` is: the inventory card
 * carries `container-inline`, which brings layout containment, and a contained
 * element becomes the containing block for `position: fixed` children. Rendered
 * in place this would be sized to the card and clipped by its `overflow-hidden`.
 */

type ConvertToNftProps = {
    nft: NFT
    onClose: () => void
    /** Told once a conversion is queued, so the inventory can re-read. */
    onQueued?: () => void
}

type Phase =
    | 'loading'
    | 'needs-wallet'
    | 'offer'
    | 'working'
    | 'minting'
    | 'minted'
    | 'pending'
    | 'refunded'
    | 'unavailable'
    | 'error'

export const ConvertToNft = ({ nft, onClose, onQueued }: ConvertToNftProps) => {
    const [phase, setPhase] = useState<Phase>('loading')
    const [price, setPrice] = useState<MintPrice | null>(null)
    const [message, setMessage] = useState<string | null>(null)
    const [mintAddress, setMintAddress] = useState<string | null>(null)
    /** Where the asset should land. Defaulted to their payout wallet, which is
     *  the first one the server sends, but changeable before paying. */
    const [wallet, setWallet] = useState<string>('')
    const [cluster, setCluster] = useState<string>('')

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }
        document.addEventListener('keydown', onKey)
        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.removeEventListener('keydown', onKey)
            document.body.style.overflow = previous
        }
    }, [onClose])

    // The price and any conversion already in flight, together: offering the
    // button to somebody who has already paid would charge them twice for one
    // asset, and the server refuses that but only after they have pressed it.
    useEffect(() => {
        let cancelled = false
        Promise.all([mintPrice(), mintStatus(nft.id)])
            .then(([cost, status]) => {
                if (cancelled) return
                setPrice(cost)
                setWallet(cost.wallets[0]?.pubkey ?? '')
                if (status.pending) setPhase('pending')
                else if (!cost.enabled) setPhase('unavailable')
                else if (!cost.has_wallet) setPhase('needs-wallet')
                else setPhase('offer')
            })
            .catch(() => {
                if (!cancelled) {
                    setMessage('Could not reach the server.')
                    setPhase('error')
                }
            })
        return () => {
            cancelled = true
        }
    }, [nft.id])

    const convert = async () => {
        setPhase('working')
        try {
            const result = await requestMint(nft.id, wallet || undefined)
            onQueued?.()
            // Paid. From here the panel watches rather than guesses: the mint
            // happens in a worker, so the only honest thing to show is a wait
            // that ends when the chain says it ended.
            if (result.status === 'Minted') setPhase('minted')
            else setPhase('minting')
        } catch (exc) {
            setMessage(exc instanceof SkinMintError ? exc.message : 'Could not start the conversion.')
            setPhase('error')
        }
    }

    // Poll while the worker does its half. Stops on the outcome, not on a
    // timer, so a failure ends the wait instead of spinning forever -- and the
    // interval is cleared on unmount, so closing the panel stops the requests.
    useEffect(() => {
        if (phase !== 'minting') return

        const timer = setInterval(async () => {
            try {
                const state = await mintStatus(nft.id)
                if (state.pending) return
                if (state.status === 'Minted') {
                    setMintAddress(state.mint ?? null)
                    setCluster(state.explorer_cluster ?? '')
                    setPhase('minted')
                    onQueued?.()
                } else if (state.status) {
                    setMessage(state.error ?? 'The mint did not complete.')
                    setPhase(state.refunded ? 'refunded' : 'error')
                }
            } catch {
                // A dropped poll is not a failed mint. Keep watching.
            }
        }, 2500)

        return () => clearInterval(timer)
    }, [phase, nft.id, onQueued])

    const cost = price ? `${price.price} ${price.currency}` : null

    return createPortal(
        <div
            className='fixed inset-0 z-[1000] grid place-items-center bg-black/80 p-4 text-base'
            onClick={onClose}
        >
            <div
                className='w-full max-w-md rounded border border-amber-400/40 bg-neutral-900 p-5'
                onClick={(event) => event.stopPropagation()}
            >
                <div className='flex flex-row items-center gap-4'>
                    <img
                        src={assetAnimationUrl(nft) ?? assetUrl(nft)}
                        alt={itemName(nft)}
                        onError={(event) => {
                            ;(event.currentTarget as HTMLImageElement).src = assetUrl(nft) ?? ''
                        }}
                        className='h-20 w-20 shrink-0 rounded border border-white/10 bg-black/30 object-contain'
                    />
                    <div className='flex min-w-0 flex-col'>
                        <span className='truncate text-sm font-bold'>{itemName(nft)}</span>
                        <span className='text-xs text-white/50'>Not an NFT yet</span>
                    </div>
                </div>

                <p className='mt-4 text-base leading-snug'>
                    Want to truly own your skin and trade with other players on the BR1
                    marketplace?
                </p>
                <p className='mt-1 text-sm text-white/60'>Convert the skin to a NFT</p>

                {phase === 'offer' && price && price.wallets.length > 0 && (
                    <div className='mt-4 flex flex-col gap-1'>
                        <span className='text-xs uppercase tracking-wide text-white/40'>
                            Send it to
                        </span>
                        {price.wallets.length === 1 ? (
                            // One wallet is not a choice, and a dropdown with a
                            // single option reads as one you failed to make.
                            <span className='break-all font-mono text-xs text-white/70'>
                                {price.wallets[0].pubkey}
                            </span>
                        ) : (
                            <select
                                value={wallet}
                                onChange={(event) => setWallet(event.target.value)}
                                className='w-full rounded border border-white/20 bg-black/40 p-2 font-mono text-xs'
                            >
                                {price.wallets.map((option) => (
                                    <option key={option.pubkey} value={option.pubkey}>
                                        {option.pubkey.slice(0, 8)}…{option.pubkey.slice(-6)}
                                        {option.name ? ` — ${option.name}` : ''}
                                        {option.is_payout ? ' (payout)' : ''}
                                    </option>
                                ))}
                            </select>
                        )}
                        <span className='text-[11px] text-white/40'>
                            The NFT is minted straight into this wallet and cannot be moved back.
                        </span>
                    </div>
                )}

                {phase === 'minting' && (
                    <div className='mt-4 flex flex-row items-center gap-3 rounded border border-white/10 p-3'>
                        <FontAwesomeIcon icon={faSpinner} spin className='text-amber-400' />
                        <div className='flex flex-col'>
                            <span className='text-sm'>Minting your NFT…</span>
                            <span className='text-xs text-white/50'>
                                Paid. Writing it to Solana and sending it to your wallet — this
                                usually takes a few seconds. The skin keeps working and keeps
                                earning meanwhile.
                            </span>
                        </div>
                    </div>
                )}

                {phase === 'refunded' && (
                    <p className='mt-4 rounded border border-white/10 p-2 text-xs text-white/70'>
                        The mint did not go through, so you have been refunded in full. Nothing
                        about your skin has changed.
                    </p>
                )}

                {phase === 'needs-wallet' && (
                    <div className='mt-4 flex flex-col gap-2 rounded border border-white/10 p-3'>
                        <span className='text-sm'>
                            First you need a Solana wallet — that is where the NFT is sent.
                        </span>
                        <ol className='ml-4 list-decimal text-xs leading-relaxed text-white/60'>
                            <li>
                                Install Phantom from{' '}
                                <a
                                    href='https://phantom.app/download'
                                    target='_blank'
                                    rel='noreferrer'
                                    className='text-amber-400 underline hover:text-amber-300'
                                >
                                    phantom.app/download
                                </a>{' '}
                                — it is a free browser extension and phone app.
                            </li>
                            <li>Open it and choose “Create a new wallet”.</li>
                            <li>
                                Write the recovery phrase down and keep it. Nobody at BR1 can
                                recover it for you.
                            </li>
                            <li>Come back and link it on your Account page.</li>
                        </ol>
                        <a
                            href='/account'
                            className='mt-1 self-start rounded bg-amber-500/90 px-4 py-2 text-sm font-black uppercase tracking-wide text-black hover:bg-amber-400'
                        >
                            Link a wallet
                        </a>
                        <span className='text-[11px] text-white/40'>
                            Nothing has been charged.
                        </span>
                    </div>
                )}

                {phase === 'minted' && (
                    <div className='mt-4 flex flex-col gap-1 rounded border border-green-400/40 bg-green-400/5 p-3'>
                        <span className='text-sm font-bold text-green-400'>Finished</span>
                        <span className='text-xs text-white/70'>
                            This is your NFT now and it has been sent to your linked wallet. It
                            still works in game and it is still earning.
                        </span>
                        {mintAddress && (
                            <a
                                href={solscanUrl(mintAddress, cluster)}
                                target='_blank'
                                rel='noreferrer'
                                title={mintAddress}
                                className='mt-1 break-all font-mono text-[10px] text-amber-400 underline hover:text-amber-300'
                            >
                                View on Solscan — {mintAddress.slice(0, 10)}…
                                {mintAddress.slice(-6)}
                            </a>
                        )}
                    </div>
                )}

                {phase === 'pending' && (
                    <p className='mt-4 rounded border border-white/10 p-2 text-xs text-white/60'>
                        This skin is already being converted. You have not been charged again.
                    </p>
                )}

                {phase === 'unavailable' && (
                    <p className='mt-4 rounded border border-white/10 p-2 text-xs text-white/60'>
                        Converting is not switched on yet. Nothing has been charged.
                    </p>
                )}

                {phase === 'error' && (
                    <p className='mt-4 rounded border border-red-400/40 bg-red-400/5 p-2 text-xs text-white/70'>
                        {message} Nothing has been charged.
                    </p>
                )}

                <div className='mt-5 flex flex-row items-center justify-between gap-3'>
                    <span className='text-xs text-white/40'>
                        {phase === 'offer' && cost ? `Costs ${cost}` : ''}
                    </span>
                    <div className='flex flex-row items-center gap-3'>
                        <button
                            onClick={onClose}
                            className='text-xs uppercase tracking-wide text-white/50 hover:text-white'
                        >
                            {phase === 'offer' ? 'Not now' : phase === 'minting' ? 'Hide' : 'Close'}
                        </button>
                        {(phase === 'offer' || phase === 'working') && (
                            <button
                                onClick={convert}
                                disabled={phase === 'working'}
                                className='flex flex-row items-center gap-2 rounded bg-amber-500/90 px-4 py-2 text-sm font-black uppercase tracking-wide text-black hover:bg-amber-400 disabled:opacity-60'
                            >
                                Convert{cost ? ` — ${cost}` : ''}
                                {phase === 'working' && <FontAwesomeIcon icon={faSpinner} spin />}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    )
}

export default ConvertToNft
